"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Badge,
  Skeleton,
  Alert,
  Tooltip,
  Stack,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  MenuItem,
  Menu,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  GroupOutlined,
  CheckCircleOutline,
  Close,
  MoreVert,
  BusinessOutlined,
  CategoryOutlined,
  CalendarTodayOutlined,
  PercentOutlined,
  NotesOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { StatusChip } from "./StatusChip";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { InvitePartnerModal } from "./InvitePartnerForm";
import AddReferralDialog from "../../bookings/add-booking2/Components/AddReferralDialog";
import AdminPayoutRequestsTable from "./AdminPayoutRequestTable";
import AdminPayoutDetailDialog from "./AdminPayoutDetails";

// ─── API shape ────────────────────────────────────────────────────────────────

interface PaymentDetail {
  id: number;
  payment_method: string;
  bank_name: string | null;
  bank_account_number: string | null;
  account_holder_name: string | null;
  created_at: string;
  updated_at: string;
  refferal_program: number;
}

interface ReferralProgram {
  id: number;
  payment_detail: PaymentDetail | null;
  total_referrals: number;
  conversion_rate: number;
  referral_name?: string | null;
  referral_surname?: string | null;
  business_name: string;
  category: string;
  status: "pending" | "approved" | "rejected" | "active" | "inactive";
  agreed_to_terms: boolean;
  business_address: string | null;
  commission_rate: number;
  special_request: string | null;
  internal_notes: string | null;
  created_at: string;
  referral_partner: number | null;
}

interface PayoutRequest {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  wallet: number;
  wallet_balance: string;
  amount: string;
  method: string;
  payout_account: number;
  payout_account_details: {
    method: string;
    ecocash_number?: string;
    bank_name?: string;
    account_number?: string;
    account_name?: string;
  } | null;
  payout_account_snapshot: {
    method: string;
    masked_ecocash_number?: string;
    bank_name?: string;
    masked_account_number?: string;
    account_name?: string;
  };
  status: "PENDING" | "APPROVED" | "REJECTED" | "COMPLETED";
  requested_at: string;
  approved_by: number | null;
  processed_by: number | null;
  admin_note: string | null;
  completed_at: string | null;
  payment_proofs: {
    id: number;
    file: string;
    note: string | null;
    uploaded_at: string;
  }[];
}
interface DashboardStats {
  total_referrals: number;
  completed_referrals: number;
  conversion_rate: number;
  active_partners: number;
  referral_revenue: string;
  commission_paid: string;
  net_profit: string;
}

type Applicant = ReferralProgram;

interface ActivityLogUser {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface ActivityLog {
  id: number;
  user_id: number;
  user: ActivityLogUser;
  action: string;
  target_type: string;
  target_id: number;
  target_name: string;
  created_at: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatOrgCategory(cat: string) {
  return (cat || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getApplicantInitials(applicant: Applicant) {
  const first = (applicant.referral_name ?? applicant.business_name ?? "")[0];
  const last = (applicant.referral_surname ?? "")[0];
  return [first, last].filter(Boolean).join("").toUpperCase() || "?";
}

function getApplicantDisplayName(applicant: Applicant) {
  const full = [applicant.referral_name, applicant.referral_surname]
    .filter(Boolean)
    .join(" ");
  return full || applicant.business_name || "—";
}

function getUserInitials(user: ActivityLogUser) {
  return (
    [(user.first_name ?? "")[0], (user.last_name ?? "")[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?"
  );
}

function getUserDisplayName(user: ActivityLogUser) {
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return full || user.email || "Unknown";
}

function formatShortDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
}

function formatFullDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatRelativeTime(dateStr: string) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatActivityText(log: ActivityLog) {
  const user = getUserDisplayName(log.user);
  const action = (log.action || "").toLowerCase().replace(/_/g, " ");
  const target = log.target_name || `${log.target_type} #${log.target_id}`;
  return `${user} ${action} — ${target}`;
}

const AVATAR_COLORS = [
  "#1a237e",
  "#4a148c",
  "#006064",
  "#1b5e20",
  "#bf360c",
  "#37474f",
  "#4e342e",
  "#283593",
];
function avatarColor(id: number) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

// ─── ReviewModal ─────────────────────────────────────────────────────────────

interface ReviewModalProps {
  open: boolean;
  partner: ReferralProgram | null;
  onClose: () => void;
  onAction: (
    partner: ReferralProgram,
    newStatus: "approved" | "rejected",
  ) => Promise<void>;
  actionLoading: number | null;
  /** If true, shows both approve & reject. If false (already decided), shows only the opposite action. */
  mode: "applicant" | "re-review";
  color400: string;
  color10: string;
}

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
    <Box
      sx={{
        color: "text.disabled",
        mt: 0.2,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        variant="caption"
        sx={{ color: "text.disabled", display: "block", mb: 0.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

const ReviewModal: React.FC<ReviewModalProps> = ({
  open,
  partner,
  onClose,
  onAction,
  actionLoading,
  mode,
  color400,
  color10,
}) => {
  if (!partner) return null;

  const isLoading = actionLoading === partner.id;
  const displayName = getApplicantDisplayName(partner);
  const initials = getApplicantInitials(partner);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: "rgba(255,255,255,0.15)",
              fontSize: 18,
              fontWeight: 700,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, display: "block", mt: 0.3 }}
            >
              {formatOrgCategory(partner.category)}
            </Typography>
            <Box sx={{ mt: 0.8 }}>
              {mode === "applicant" ? (
                <Chip
                  label="Pending Review"
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,193,7,0.2)",
                    color: "#ffe082",
                    border: "1px solid rgba(255,193,7,0.3)",
                    fontSize: "0.65rem",
                    height: 20,
                  }}
                />
              ) : (
                <Chip
                  label={
                    partner.status.charAt(0).toUpperCase() +
                    partner.status.slice(1)
                  }
                  size="small"
                  sx={{
                    bgcolor:
                      partner.status === "approved"
                        ? "rgba(16,185,129,0.2)"
                        : "rgba(239,68,68,0.2)",
                    color:
                      partner.status === "approved" ? "#6ee7b7" : "#fca5a5",
                    border: `1px solid ${partner.status === "approved" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                    fontSize: "0.65rem",
                    height: 20,
                  }}
                />
              )}
            </Box>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <InfoRow
          icon={<BusinessOutlined sx={{ fontSize: 16 }} />}
          label="Business Name"
          value={partner.business_name}
        />
        <InfoRow
          icon={<CategoryOutlined sx={{ fontSize: 16 }} />}
          label="Category"
          value={formatOrgCategory(partner.category)}
        />
        <InfoRow
          icon={<PercentOutlined sx={{ fontSize: 16 }} />}
          label="Commission Rate"
          value={
            partner.commission_rate != null
              ? `${partner.commission_rate}%`
              : null
          }
        />
        <InfoRow
          icon={<CalendarTodayOutlined sx={{ fontSize: 16 }} />}
          label="Applied On"
          value={formatFullDate(partner.created_at)}
        />

        {partner.business_address && (
          <InfoRow
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            }
            label="Business Address"
            value={partner.business_address}
          />
        )}

        {partner.special_request && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <InfoRow
              icon={<NotesOutlined sx={{ fontSize: 16 }} />}
              label="Special Request"
              value={
                <Typography
                  variant="body2"
                  sx={{
                    bgcolor: "#f8f9fa",
                    p: 1.5,
                    borderRadius: "10px",
                    fontStyle: "italic",
                    color: "text.secondary",
                    fontWeight: 400,
                  }}
                >
                  &ldquo;{partner.special_request}&rdquo;
                </Typography>
              }
            />
          </>
        )}

        {/* Payment method info if present */}
        {partner.payment_detail && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                display: "block",
                mb: 1.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 600,
              }}
            >
              Payment Details
            </Typography>
            <InfoRow
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                </svg>
              }
              label="Payment Method"
              value={partner.payment_detail.payment_method}
            />
            {partner.payment_detail.account_holder_name && (
              <InfoRow
                icon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                }
                label="Account Holder"
                value={partner.payment_detail.account_holder_name}
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          gap: 1.5,
          flexDirection: "column",
        }}
      >
        {/* Context note for re-review */}
        {mode === "re-review" && (
          <Box
            sx={{
              width: "100%",
              bgcolor: partner.status === "rejected" ? "#fff3e0" : "#e8f5e9",
              borderRadius: "10px",
              px: 2,
              py: 1.2,
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: partner.status === "rejected" ? "#e65100" : "#2e7d32",
              }}
            >
              {partner.status === "rejected"
                ? "This partner was previously rejected. You can approve them here."
                : "This partner is currently approved. You can reject them here."}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          {isLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                py: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Show Reject if: pending applicant OR currently approved */}
              {(mode === "applicant" || partner.status === "approved") && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Cancel />}
                  onClick={() => onAction(partner, "rejected")}
                  sx={{
                    borderColor: "#ff4d6d",
                    color: "#ff4d6d",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#fff0f3",
                      borderColor: "#ff4d6d",
                    },
                  }}
                >
                  Reject
                </Button>
              )}

              {/* Show Approve if: pending applicant OR currently rejected */}
              {(mode === "applicant" || partner.status === "rejected") && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CheckCircle />}
                  onClick={() => onAction(partner, "approved")}
                  sx={{
                    bgcolor: "#00b894",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#00a381",
                      boxShadow: "none",
                    },
                  }}
                >
                  Approve
                </Button>
              )}
            </>
          )}
        </Box>

        {/* View full profile link */}
        <Button
          size="small"
          fullWidth
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── stat card ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  loading?: boolean;
  color?: string;
}> = ({ label, value, icon, loading, color = "#1976d2" }) => {
  const [displayValue, setDisplayValue] = useState<number | string>(value);

  useEffect(() => {
    const numericValue =
      typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/[^0-9.]/g, ""));

    if (isNaN(numericValue) || loading) {
      setDisplayValue(value);
      return;
    }

    let start = 0;
    const duration = 1000;
    const increment = numericValue / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        const formatted = Math.floor(start);
        if (typeof value === "string" && value.includes("$")) {
          setDisplayValue(`$${formatted.toLocaleString()}`);
        } else if (typeof value === "string" && value.includes("%")) {
          setDisplayValue(`${formatted}%`);
        } else {
          setDisplayValue(formatted);
        }
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, loading]);

  return (
    <Card
      sx={{
        bgcolor: "#fff",
        flex: 1,
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "140px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "transform 0.3s ease",
        border: "1px solid #f0f0f0",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              color: color,
              display: "flex",
              p: 1,
              borderRadius: "10px",
              bgcolor: `${color}10`,
            }}
          >
            {icon}
          </Box>
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, fontSize: "2rem", color: "text.primary" }}
          >
            {displayValue}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ─── main dashboard ───────────────────────────────────────────────────────────

export default function ReferralProgramAdmin() {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // ── Theme colours (same pattern as BookingModal) ──────────────────────────
  const color10 = reduxTheme.primary_colors?.[10] ?? "#f5ede9";
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";

  // ── partners state ────────────────────────────────────────────────────────
  const [partners, setPartners] = useState<ReferralProgram[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [dashStatsLoading, setDashStatsLoading] = useState(true);

  const [pendingPayouts, setPendingPayouts] = useState<PayoutRequest[]>([]);
  const [pendingPayoutsLoading, setPendingPayoutsLoading] = useState(true);
  const [payoutDetailOpen, setPayoutDetailOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(
    null,
  );

  // ── applicants state ──────────────────────────────────────────────────────
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [applicantsLoading, setApplicantsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [addPartnerOpen, setAddPartnerOpen] = useState(false);
  // ── review modal state ────────────────────────────────────────────────────
  const [reviewModal, setReviewModal] = useState<{
    open: boolean;
    partner: ReferralProgram | null;
    mode: "applicant" | "re-review";
  }>({ open: false, partner: null, mode: "applicant" });

  // ── table row menu state ──────────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    partner: ReferralProgram;
  } | null>(null);

  // ── activity logs state ───────────────────────────────────────────────────
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // ── fetch partners ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append("page", String(page + 1));
        params.append("page_size", String(rowsPerPage));
        if (search) params.append("search", search);
        if (statusFilter) params.append("status", statusFilter);

        const response = await apiService.get<{
          results: ReferralProgram[];
          count: number;
        }>(`/api/refferal-program/refferal-programs/?${params.toString()}`);

        setPartners(response.data.results ?? []);
        setTotalCount(response.data.count ?? 0);
      } catch {
        setError("Failed to load referral partners. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, [page, rowsPerPage, search, statusFilter]);

  const fetchPendingPayouts = useCallback(async () => {
    try {
      setPendingPayoutsLoading(true);
      const res = await apiService.get<{ results: PayoutRequest[] }>(
        "/api/wallet/admin/payout-requests/?status=PENDING&limit=4",
      );
      setPendingPayouts(res.data.results ?? []);
    } catch {
      // silently fail
    } finally {
      setPendingPayoutsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayouts();
  }, [fetchPendingPayouts]);
  // ── fetch pending applicants ──────────────────────────────────────────────
  const fetchApplicants = async () => {
    try {
      setApplicantsLoading(true);
      const response = await apiService.get<{ results: ReferralProgram[] }>(
        `/api/refferal-program/refferal-programs/?status=pending&page_size=4`,
      );
      setApplicants(response.data.results ?? []);
    } catch {
      // silently fail
    } finally {
      setApplicantsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, []);

  useEffect(() => {
    const fetchDashStats = async () => {
      try {
        setDashStatsLoading(true);
        const response = await apiService.get<DashboardStats>(
          `/api/refferal-program/refferal-programs/admin-dashbaord-stats/`,
        );
        setDashStats(response.data);
      } catch (err) {
        console.error("fetchDashStats failed:", err);
      } finally {
        setDashStatsLoading(false);
      }
    };
    fetchDashStats();
  }, []);

  // ── fetch activity logs ───────────────────────────────────────────────────
  const fetchActivityLogs = useCallback(async () => {
    try {
      setActivityLoading(true);
      const response = await apiService.get<{
        results: ActivityLog[];
        count: number;
      }>(
        `/api/refferal-program/refferal-activitylogs/?ordering=-created_at&page_size=5`,
      );
      setActivityLogs(response.data.results ?? []);
    } catch {
      // silently fail
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  // ── unified approve / reject handler ─────────────────────────────────────
  const handleAction = async (
    partner: ReferralProgram,
    newStatus: "approved" | "rejected",
  ) => {
    setActionLoading(partner.id);
    try {
      await apiService.patch(
        `/api/refferal-program/refferal-programs/${partner.id}/`,
        { status: newStatus },
      );
      const displayName = getApplicantDisplayName(partner);
      setSnackbar({
        open: true,
        message: `${displayName} ${newStatus === "approved" ? "approved" : "rejected"} successfully.`,
      });

      // Remove from applicants card if was pending
      setApplicants((prev) => prev.filter((a) => a.id !== partner.id));

      // Update table row status
      setPartners((prev) =>
        prev.map((p) =>
          p.id === partner.id
            ? { ...p, status: newStatus as ReferralProgram["status"] }
            : p,
        ),
      );

      // Close modal
      setReviewModal({ open: false, partner: null, mode: "applicant" });

      // Refresh activity logs
      fetchActivityLogs();
    } catch {
      setSnackbar({ open: true, message: "Action failed. Please try again." });
    } finally {
      setActionLoading(null);
    }
  };

  // ── open modal helpers ────────────────────────────────────────────────────
  const openApplicantReview = (applicant: Applicant) => {
    setReviewModal({ open: true, partner: applicant, mode: "applicant" });
  };

  const openReReview = (partner: ReferralProgram) => {
    setMenuAnchor(null);
    setReviewModal({ open: true, partner, mode: "re-review" });
  };

  // ── derived stats ─────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: totalCount,
      pending: partners.filter((p) => p.status === "pending").length,
      approved: partners.filter((p) => p.status === "approved").length,
      rejected: partners.filter((p) => p.status === "rejected").length,
    }),
    [partners, totalCount],
  );

  // ── handlers ──────────────────────────────────────────────────────────────
  const openDrawer = (partner: ReferralProgram) =>
    navigate(`/referrals/view/${partner.id}`);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, minHeight: "100vh", boxShadow: "none" }}>
      {/* ── Stats Cards Row ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          label="Total Referrals"
          value={dashStats?.total_referrals ?? 0}
          icon={<GroupOutlined fontSize="small" />}
          loading={dashStatsLoading}
          color="#8b5cf6"
        />
        <StatCard
          label="Conversion Rate"
          value={dashStats ? `${dashStats.conversion_rate}%` : "0%"}
          icon={<CheckCircleOutline fontSize="small" />}
          loading={dashStatsLoading}
          color="#10b981"
        />
        <StatCard
          label="Active Partners"
          value={dashStats?.active_partners ?? 0}
          icon={<CheckCircleOutline fontSize="small" />}
          loading={dashStatsLoading}
          color="#3b82f6"
        />
        <StatCard
          label="Referral Revenue"
          value={
            dashStats
              ? `$${parseFloat(dashStats.referral_revenue).toLocaleString()}`
              : "$0"
          }
          icon={<GroupOutlined fontSize="small" />}
          loading={dashStatsLoading}
          color="#f59e0b"
        />
      </Box>

      {/* ── Middle Section ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 2,
          mb: 3,
          alignItems: "start",
        }}
      >
        {/* Payout Requests */}

        <Card
          sx={{ borderRadius: "20px", boxShadow: "none", minHeight: "320px" }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Payout Requests
                </Typography>
                {!pendingPayoutsLoading && pendingPayouts.length > 0 && (
                  <Badge
                    badgeContent={pendingPayouts.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 9,
                        height: 16,
                        minWidth: 16,
                        ml: 2,
                        color: "#fff",
                      },
                    }}
                  />
                )}
              </Box>
              <Button
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#666",
                  fontSize: "0.75rem",
                }}
                onClick={() => setActiveTab(1)}
              >
                View all
              </Button>
            </Box>

            {pendingPayoutsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : pendingPayouts.length === 0 ? (
              <Box sx={{ textAlign: "center", pt: 4, color: "text.secondary" }}>
                <Typography variant="caption">
                  No pending payout requests
                </Typography>
              </Box>
            ) : (
              pendingPayouts.map((payout) => (
                <Box
                  key={payout.id}
                  onClick={() => {
                    setSelectedPayout(payout);
                    setPayoutDetailOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.2,
                    px: 1.2,
                    mb: 1,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "14px",
                    minHeight: "60px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      backgroundColor: "#ede7f6",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "#1a237e",
                        fontSize: 12,
                      }}
                    >
                      {(payout.user_name ?? "?")
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2) || "?"}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {payout.user_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.68rem" }}
                      >
                        ${parseFloat(payout.amount).toFixed(2)} ·{" "}
                        {payout.method} ·{" "}
                        {new Date(payout.requested_at).toLocaleDateString(
                          "en-GB",
                          { day: "2-digit", month: "short" },
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        sx={{ color: "#666", p: 0.3 }}
                        onClick={() => {
                          setSelectedPayout(payout);
                          setPayoutDetailOpen(true);
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 18, color: "#00b894" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        {/* New Applicants — clicking opens review modal */}
        <Card
          sx={{ borderRadius: "20px", boxShadow: "none", minHeight: "320px" }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  New Applicants
                </Typography>
                {!applicantsLoading && applicants.length > 0 && (
                  <Badge
                    badgeContent={applicants.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 9,
                        height: 16,
                        minWidth: 16,
                        ml: 2,
                        color: "#fff",
                      },
                    }}
                  />
                )}
              </Box>
              <Button
                size="small"
                sx={{
                  textTransform: "none",
                  color: "#666",
                  fontSize: "0.75rem",
                }}
                onClick={() => handleStatusFilterChange("pending")}
              >
                View all
              </Button>
            </Box>

            {applicantsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : applicants.length === 0 ? (
              <Box sx={{ textAlign: "center", pt: 4, color: "text.secondary" }}>
                <Typography variant="caption">No pending applicants</Typography>
              </Box>
            ) : (
              applicants.map((applicant) => (
                <Box
                  key={applicant.id}
                  onClick={() => openApplicantReview(applicant)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.2,
                    px: 1.2,
                    mb: 1,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "14px",
                    minHeight: "60px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      backgroundColor: "#ede7f6",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "#d6e4ff",
                        color: "#3b5bdb",
                        fontSize: 12,
                      }}
                    >
                      {getApplicantInitials(applicant)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {getApplicantDisplayName(applicant)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.68rem" }}
                      >
                        {formatOrgCategory(applicant.category)} ·{" "}
                        {formatShortDate(applicant.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actionLoading === applicant.id ? (
                      <CircularProgress size={18} sx={{ mx: 0.5 }} />
                    ) : (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            sx={{ color: "#00b894", p: 0.3 }}
                            onClick={() => handleAction(applicant, "approved")}
                          >
                            <CheckCircle sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            sx={{ color: "#ff4d6d", p: 0.3 }}
                            onClick={() => handleAction(applicant, "rejected")}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        {/* Third column — Recent Activity (live) */}
        <Card
          sx={{
            borderRadius: "20px",
            boxShadow: "none",
            bgcolor: "transparent",
            overflow: "visible",
          }}
        >
          <CardContent
            sx={{
              p: 0,
              "&:last-child": { pb: 0 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setAddPartnerOpen(true)} // ← add this
                sx={{
                  textTransform: "none",
                  borderColor: "#795548",
                  color: "#795548",
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  height: "44px",
                }}
              >
                Add Partner
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setInviteOpen(true)}
                sx={{
                  textTransform: "none",
                  bgcolor: "#6d4c41",
                  "&:hover": { bgcolor: "#5d4037" },
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  height: "44px",
                }}
              >
                Invite Partner
              </Button>
              <InvitePartnerModal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                inviteLink={`${window.location.origin}/referral-program`}
                color400={color400}
                color10={color10}
              />
              <AddReferralDialog
                open={addPartnerOpen}
                onClose={() => setAddPartnerOpen(false)}
                onSaved={() => {
                  setAddPartnerOpen(false);
                  // Refresh the partners table and applicants
                  setPage(0);
                  fetchApplicants();
                  fetchActivityLogs();
                }}
              />
            </Box>

            <Box
              sx={{
                bgcolor: "white",
                borderRadius: "20px",
                p: 2.5,
                maxHeight: "275px",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ fontSize: "0.95rem" }}
                >
                  Recent Activity
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={fetchActivityLogs}
                    disabled={activityLoading}
                    sx={{ color: "#666" }}
                  >
                    {activityLoading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                      </svg>
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {activityLoading && (
                <Box>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        gap: 2,
                        mb: 2,
                        alignItems: "center",
                      }}
                    >
                      <Skeleton
                        variant="circular"
                        width={32}
                        height={32}
                        sx={{ flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={14} />
                        <Skeleton variant="text" width="40%" height={12} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {!activityLoading && activityLogs.length === 0 && (
                <Box
                  sx={{ textAlign: "center", pt: 3, color: "text.secondary" }}
                >
                  <Typography variant="caption">No recent activity</Typography>
                </Box>
              )}

              {!activityLoading &&
                activityLogs.map((log) => (
                  <Box
                    key={log.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: avatarColor(log.user_id),
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {getUserInitials(log.user)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          lineHeight: 1.3,
                          color: "#444",
                          fontSize: "0.75rem",
                        }}
                      >
                        {formatActivityText(log)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        {formatRelativeTime(log.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ── Partners Table ── */}
      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            {["Partners", "Payouts"].map((label, idx) => (
              <Box
                key={label}
                onClick={() => setActiveTab(idx)}
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: "20px",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  fontWeight: activeTab === idx ? 600 : 400,
                  bgcolor: activeTab === idx ? "#e8e8e8" : "transparent",
                  color: activeTab === idx ? "text.primary" : "text.secondary",
                  border: "1px solid",
                  borderColor: activeTab === idx ? "#ccc" : "transparent",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: "#f0f0f0" },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          {activeTab === 0 && (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 2,
                borderBottom: "1px solid #eee",
                pb: 1,
              }}
            >
              {[
                { label: "All", count: totalCount, value: "" },
                { label: "Approved", count: stats.approved, value: "approved" },
                {
                  label: "New Applicants",
                  count: stats.pending,
                  value: "pending",
                },
                { label: "Rejected", count: stats.rejected, value: "rejected" },
              ].map((item) => (
                <Typography
                  key={item.value}
                  variant="body2"
                  onClick={() => handleStatusFilterChange(item.value)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: statusFilter === item.value ? 700 : 400,
                    color:
                      statusFilter === item.value
                        ? "text.primary"
                        : "text.secondary",
                    borderBottom:
                      statusFilter === item.value
                        ? "2px solid #1a237e"
                        : "2px solid transparent",
                    pb: 0.5,
                    mb: "-1px",
                  }}
                >
                  {item.label} {item.count}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>

        {activeTab === 0 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                {error}
              </Alert>
            )}

            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Partner",
                      "Business",
                      "Referrals",
                      "Conversion",
                      "Commission",
                      "Status",
                      "", // actions column
                    ].map((col, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          color: "text.secondary",
                          fontWeight: 500,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          whiteSpace: "nowrap",
                          py: 1.5,
                          bgcolor: "#fff",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: rowsPerPage }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((__, j) => (
                            <TableCell key={j}>
                              <Skeleton variant="rounded" height={20} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : partners.map((partner) => (
                        <TableRow
                          key={partner.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            "&:last-child td": { border: 0 },
                          }}
                          onClick={() => openDrawer(partner)}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="primary"
                              fontWeight={500}
                            >
                              {[partner.referral_name, partner.referral_surname]
                                .filter(Boolean)
                                .join(" ") ||
                                partner.business_name ||
                                "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatOrgCategory(partner.category)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={partner.business_name}
                              placement="top"
                            >
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 180, display: "block" }}
                              >
                                {partner.business_name || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partner.total_referrals ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partner.conversion_rate != null
                                ? `${partner.conversion_rate.toFixed(1)}%`
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partner.commission_rate != null
                                ? `${partner.commission_rate}%`
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              type="partner"
                              status={partner.status}
                            />
                          </TableCell>

                          {/* ── Actions column: ⋮ menu for approved/rejected ── */}
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: 40, pr: 1 }}
                          >
                            {(partner.status === "approved" ||
                              partner.status === "rejected") && (
                              <Tooltip
                                title={
                                  partner.status === "rejected"
                                    ? "Review & approve"
                                    : "Change status"
                                }
                              >
                                <IconButton
                                  size="small"
                                  sx={{ color: "text.disabled" }}
                                  onClick={(e) =>
                                    setMenuAnchor({
                                      el: e.currentTarget,
                                      partner,
                                    })
                                  }
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  {!loading && partners.length === 0 && !error && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 6, color: "text.secondary" }}
                      >
                        No partners match your filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}

        {activeTab === 1 && <AdminPayoutRequestsTable />}
      </Card>

      {/* ── Row actions menu (for approved / rejected partners) ── */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 180 } }}
      >
        <MenuItem
          onClick={() => menuAnchor && openReReview(menuAnchor.partner)}
          sx={{ fontSize: "0.85rem", gap: 1 }}
        >
          {menuAnchor?.partner.status === "rejected" ? (
            <>
              <CheckCircle fontSize="small" sx={{ color: "#00b894" }} />
              Review & Approve
            </>
          ) : (
            <>
              <Cancel fontSize="small" sx={{ color: "#ff4d6d" }} />
              Review & Reject
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              openDrawer(menuAnchor.partner);
              setMenuAnchor(null);
            }
          }}
          sx={{ fontSize: "0.85rem", gap: 1, color: "text.secondary" }}
        >
          View Full Profile
        </MenuItem>
      </Menu>

      {/* ── Review Modal ── */}
      <ReviewModal
        open={reviewModal.open}
        partner={reviewModal.partner}
        mode={reviewModal.mode}
        onClose={() =>
          setReviewModal({ open: false, partner: null, mode: "applicant" })
        }
        onAction={handleAction}
        actionLoading={actionLoading}
        color400={color400}
        color10={color10}
      />

      {/* ── Snackbar feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <AdminPayoutDetailDialog
        open={payoutDetailOpen}
        payout={selectedPayout}
        onClose={() => {
          setPayoutDetailOpen(false);
          fetchPendingPayouts(); // refresh card after action
        }}
        onUpdated={(updated) => {
          setPendingPayouts((prev) =>
            prev.map((p) => (p.id === updated.id ? updated : p)),
          );
          setSelectedPayout(updated);
        }}
      />
    </Box>
  );
}
